import { API_URL } from '../config/connect';
import { cachedGet, CHINA_CACHE_TTL, CHINA_PRODUCT_CACHE_TTL } from './chinaCache';
import i18n from '../language/i18n';
import { throwResponseError, extractErrorMessageString } from '../utils/apiErrorHandler';

// All Chinese-goods traffic now goes through our backend proxy at
// `${API_URL}/api/v1/china`. The MercuryX API key stays server-side, and
// preprod vs prod is a Cloud Run env var swap — the web bundle doesn't
// care which upstream is answering.
const CHINA_API = `${API_URL}/api/v1/china`;

const authHeaders = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const parseNames = (names) => {
    if (!names) return {};
    if (typeof names === 'string') {
        try { return JSON.parse(names); } catch { return {}; }
    }
    return names;
};

// MercuryX names come as a localized object {ru,kk,en,cn}; the API spec's
// illustrated shape uses a plain `name` string. Support both — fall back to
// spreading `name` across all locales when no `names` object is present.
const asNames = (names, name) => {
    const parsed = parseNames(names);
    if (parsed && Object.keys(parsed).length) return parsed;
    if (name) return { ru: name, kk: name, en: name, cn: name };
    return {};
};

// The backend returns `{success:false, error:{error_code, message}}` on
// MercuryX upstream failures (e.g. MERCURYX_BAD_RESPONSE). Surface those as
// thrown errors instead of letting callers silently render empty.
const unwrap = (json) => {
    if (json && json.success === false) {
        throw new Error(extractErrorMessageString(json) || i18n.t('unknownError'));
    }
    return json?.data ?? json;
};

// Errors arrive in two shapes: our proxy's validation errors use
// `{ detail: { error_code, message, ... } }` (or `{ detail: "string" }`),
// while MercuryX upstream failures are wrapped as `{ error: { error_code, message } }`.
// Normalize both so the UI can render the real message (e.g. "Компания не найдена")
// and callers can branch on error_code (e.g. BANNER_SLOT_ALREADY_ACTIVE).
const extractApiError = (json, fallbackCode) => {
    const detail = json?.detail;
    const error = json?.error;
    let code = null;
    let message = null;
    let details = null;
    if (detail && typeof detail === 'object') {
        code = detail.error_code || detail.error || detail.code || null;
        message = detail.message || detail.error_message || null;
        details = detail.details || (detail.slot_id != null ? { slot_id: detail.slot_id } : null);
    } else if (typeof detail === 'string' && detail) {
        message = detail;
    } else if (error && typeof error === 'object') {
        code = error.error_code || error.error || error.code || null;
        message = error.message || null;
        details = error.details || null;
    }
    return { code: code || fallbackCode, message, details };
};

// ---------------------------------------------------------------------------
// Catalog (public, no auth)
// ---------------------------------------------------------------------------

export const getChinaCategories = async (parentId = 0) => {
    return cachedGet(`cat:${parentId || 0}`, CHINA_CACHE_TTL, async () => {
        const url = parentId
            ? `${CHINA_API}/categories?parent_id=${parentId}`
            : `${CHINA_API}/categories`;
        const res = await fetch(url);
        if (!res.ok) await throwResponseError(res);
        const json = await res.json();
        const data = unwrap(json);
        const arr = Array.isArray(data) ? data : (data?.children || data?.categories || []);
        return arr
            .filter(c => c != null)
            // Only apply the active/blocked filter when MercuryX actually
            // sends those flags — otherwise every category would be dropped.
            .filter(c => c.is_active == null || c.is_blocked == null || (c.is_active && !c.is_blocked))
            .map(c => ({
                id: c.id,
                slug: c.slug,
                names: asNames(c.names, c.name),
                products_count: c.products_count ?? c.productCount ?? c.productsCount ?? 0,
                image: c.entity?.image && !Array.isArray(c.entity.image) ? c.entity.image : (c.image || null),
                has_children: !!(c.has_children ?? c.hasChildren),
                level: c.level,
            }));
    });
};

export const getChinaProduct = async (id) => {
    return cachedGet(`prod:detail:${id}`, CHINA_PRODUCT_CACHE_TTL, async () => {
        const res = await fetch(`${CHINA_API}/products/${id}`);
        if (!res.ok) await throwResponseError(res);
        const json = await res.json();
        const data = unwrap(json);
        const images = Array.isArray(data.images) ? data.images : (data.slides || (data.image ? [data.image] : []));
        // Normalize bulk pricing: MercuryX uses {startQuantity, price}, the spec
        // example uses {minQty, price} — map both to {startQuantity, price} so
        // getCurrentPrice() and the BulkPricing component keep working.
        const rawRanges = data.priceRanges || data.bulkPricing || [];
        const priceRanges = rawRanges.map(r => ({ startQuantity: r.startQuantity ?? r.minQty, price: r.price }));
        return {
            id: data.id ?? data.productId,
            productId: data.productId ?? data.id,
            price: data.price,
            image: data.image || (images[0] || null),
            slides: data.slides || images,
            names: asNames(data.names, data.name),
            offers: (data.offers || []).map(o => ({
                ...o,
                externalSkuId: o.externalSkuId ?? o.external_sku_id ?? o.skuId ?? null,
            })),
            priceRanges,
            skuAttributes: data.skuAttributes || [],
            attributes: data.attributes || [],
            description: data.description || '',
            minQty: data.minQty ?? 1,
            amountOnSale: data.amountOnSale ?? 0,
            soldOut: data.soldOut ?? 0,
            breadcrumbs: data.breadcrumbs || [],
            categoryId: data.categoryId || data.category_id,
        };
    });
};

export const getChinaProducts = async (categoryId, page = 1, limit = 20, sort = []) => {
    const sortArr = Array.isArray(sort) ? sort : sort ? [sort] : [];
    const cacheKey = `prod:${categoryId || 0}:${page}:${limit}:${sortArr.join(',')}`;
    return cachedGet(cacheKey, CHINA_CACHE_TTL, async () => {
        const params = new URLSearchParams({ page: String(page), per_page: String(limit) });
        if (categoryId) params.set('category_id', String(categoryId));
        sortArr.forEach(s => params.append('sort', s));
        const res = await fetch(`${CHINA_API}/products?${params}`);
        if (!res.ok) await throwResponseError(res);
        const json = await res.json();
        const data = unwrap(json);
        // Backend returns a Laravel paginator: { data: { current_page, data:[...products], total, last_page } }.
        // The product array lives at data.data — check it alongside the spec's
        // `products`/`items` shapes so the listing isn't silently empty.
        const arr = Array.isArray(data) ? data : (data?.products || data?.items || data?.data || []);
        const items = arr.map(p => ({
            id: p.id ?? p.productId,
            productId: p.productId ?? p.id,
            price: p.price,
            image: p.image || (Array.isArray(p.images) ? p.images[0] : null),
            slides: p.slides || (Array.isArray(p.images) ? p.images : []),
            names: asNames(p.names, p.name),
            minQty: p.minQty ?? 1,
            amountOnSale: p.amountOnSale ?? 0,
            soldOut: p.soldOut ?? 0,
            totalSales: p.totalSales ?? 0,
            productRate: p.productRate ?? null,
        }));
        const meta = Array.isArray(data) ? null : data;
        const total = meta?.total ?? json.total ?? items.length;
        const current = meta?.page ?? meta?.current_page ?? json.page ?? page;
        const totalPages = meta?.totalPages ?? meta?.last_page ?? json.totalPages;
        const hasMore = totalPages != null
            ? current < totalPages
            : (meta?.current_page != null ? meta.current_page < meta.last_page : items.length >= limit);
        return { items, total, page: current, hasMore };
    });
};

// ---------------------------------------------------------------------------
// Cart (auth required)
// ---------------------------------------------------------------------------

export const getChinaCart = async () => {
    const res = await fetch(`${CHINA_API}/cart`, { headers: authHeaders() });
    if (!res.ok) await throwResponseError(res);
    const json = await res.json();
    // unwrap returns json?.data ?? json, so a top-level {items,count} survives
    // unchanged (callers read cart.items / cart.count) while a {success:false}
    // upstream failure is surfaced as a thrown error instead of a silent empty.
    return unwrap(json);
};

export const addChinaCartItem = async ({ productId, skuId = null, quantity = 1, minQty = null, priceAtAdd = null, titleSnapshot = null, imageUrl = null }) => {
    const res = await fetch(`${CHINA_API}/cart/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
            product_id: productId,
            sku_id: skuId,
            quantity,
            min_qty: minQty,
            price_at_add: priceAtAdd,
            title_snapshot: titleSnapshot,
            image_url: imageUrl,
        }),
    });
    if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const { code, message } = extractApiError(json, `add cart item ${res.status}`);
        throw new Error(message || code);
    }
    return res.json();
};

export const updateChinaCartItem = async (itemId, quantity) => {
    const res = await fetch(`${CHINA_API}/cart/items/${itemId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ quantity }),
    });
    if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const { code, message } = extractApiError(json, `update cart item ${res.status}`);
        throw new Error(message || code);
    }
    return res.json();
};

export const removeChinaCartItem = async (itemId) => {
    const res = await fetch(`${CHINA_API}/cart/items/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders(),
    });
    if (!res.ok) await throwResponseError(res);
    return res.json();
};

// ---------------------------------------------------------------------------
// Order create (auth required — needs phone + BIN)
// ---------------------------------------------------------------------------

export const createChinaOrder = async ({ phone, bin, cartItemIds = null }) => {
    const res = await fetch(`${CHINA_API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
            phone,
            bin,
            cart_item_ids: cartItemIds,
            consent_accepted: true,
        }),
    });
    if (!res.ok) {
        // MercuryX errors bubble through — surface them for the UI.
        const err = await throwResponseError(res).catch(e => e);
        err.details = extractApiError(err.data, `create order ${res.status}`).details || {};
        throw err;
    }
    return res.json();
};

export const getChinaOrders = async (limit = 20) => {
    const res = await fetch(`${CHINA_API}/orders?limit=${limit}`, { headers: authHeaders() });
    if (!res.ok) {
        const err = new Error(`orders ${res.status}`);
        err.status = res.status;
        throw err;
    }
    const json = await res.json();
    return unwrap(json);
};
